import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { RestoreLogo } from "@/components/RestoreLogo";
import { SiGoogle } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { queryClient, setToken } from "@/lib/queryClient";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const params = new URLSearchParams(window.location.search);
  const errorParam = params.get("error");
  const returnTo = params.get("returnTo") || "";

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(data: LoginForm) {
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) {
        if (json.requiresVerification && json.email) {
          sessionStorage.setItem("pendingVerifyEmail", json.email);
          setLocation(`/verify-email?email=${encodeURIComponent(json.email)}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`);
          return;
        }
        const description = json.error?.message || json.message || "Something went wrong. Please try again.";
        toast({ title: "Login failed", description, variant: "destructive" });
        return;
      }
      if (json.token) setToken(json.token);
      if (json.user) queryClient.setQueryData(["/api/auth/user"], json.user);
      setLocation(returnTo || "/");
    } catch {
      toast({ title: "Login failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  function handleGoogle() {
    window.location.href = "/api/auth/google";
  }

  // DISABLED 2026-09-18: Microsoft/Entra sign-in commented out (not deleted).
  // Why: the Entra Domain Services resource was deleted to stop ~$300/mo of spend and
  // telemetry showed zero Microsoft logins. Backend routes are commented out too
  // (server/auth.ts), so this button would 404. Restore both sides together.
  // function handleMicrosoft() {
  // window.location.href = "/api/auth/microsoft";
  // }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <RestoreLogo size="lg" href="/" showTagline />
          </div>

          <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
          <p className="mb-8" style={{ color: "var(--restore-text-muted)" }}>
            Don't have an account?{" "}
            <Link href="/signup" className="font-medium hover:underline" style={{ color: "var(--restore-accent)" }}>
              Sign up
            </Link>
          </p>

          {errorParam === "google_failed" && (
            <div className="mb-6 p-4 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">
              Google sign-in failed. Please try again or use email and password.
            </div>
          )}

          {/* DISABLED 2026-09-18: Microsoft/Entra sign-in error banner commented out (not
              deleted). Why: Entra Domain Services was deleted to stop ~$300/mo of spend
              and telemetry showed zero Microsoft logins. See server/auth.ts.

            {(errorParam === "microsoft_failed" || errorParam === "microsoft_not_configured") && (
              <div className="mb-6 p-4 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">
                Microsoft sign-in failed. Please try again or use email and password.
              </div>
            )}
          */}

          <div className="space-y-3 mb-6">
            <Button
              type="button"
              onClick={handleGoogle}
              variant="outline"
              className="w-full flex items-center justify-center gap-3 border-gray-600 text-white hover:bg-gray-800 font-medium h-11"
              style={{ backgroundColor: "transparent" }}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Continue with Google
            </Button>
          {/* DISABLED 2026-09-18: Microsoft/Entra sign-in button commented out (not
              deleted). Why: Entra Domain Services was deleted to stop ~$300/mo of spend
              and telemetry showed zero Microsoft logins. See server/auth.ts.

              <Button
                type="button"
                onClick={handleMicrosoft}
                variant="outline"
                className="w-full flex items-center justify-center gap-3 border-gray-600 text-white hover:bg-gray-800 font-medium h-11"
                style={{ backgroundColor: "transparent" }}
              >
                <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
                  <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
                  <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
                  <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                </svg>
                Continue with Microsoft
              </Button>
          */}
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 text-gray-500" style={{ backgroundColor: "var(--restore-dark-bg)" }}>or sign in with email</span>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Email address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-300">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Link href="/forgot-password" className="text-sm hover:underline" style={{ color: "var(--restore-accent)" }}>
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full text-gray-900 font-semibold h-11 text-base"
                style={{ backgroundColor: "var(--restore-accent)" }}
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          </Form>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-12 border-l border-gray-800">
        <div className="max-w-md text-center">
          <div className="text-6xl font-bold mb-4" style={{ color: "var(--restore-accent)" }}>+30%</div>
          <h3 className="text-2xl font-bold text-white mb-4">First-Pass Claim Acceptance</h3>
          <p style={{ color: "var(--restore-text-muted)" }}>
            RestoreAI turns field photos and notes into carrier-ready scopes and estimates — cutting claim cycles from weeks to days.
          </p>
        </div>
      </div>
    </div>
  );
}
